// libs
import { getFlatDataFromTree, map as mapTree, walk } from 'react-sortable-tree'
// constants
import { ENTITIES } from 'constants/entities'
// utils
import get from 'lodash/get'
import { safeGetOr } from './fp'
import { filter, groupBy, identity, last, map, max, orderBy, pipe, size } from 'lodash/fp'

export const getNodeKey = ({ node: { id } }) => id
export const getKey = ({ id }) => id
export const getParentKey = ({ parentId }) => parentId

export const getTreeLastPosition = (treeData) => {
  const positions = []

  walk({
    treeData,
    getNodeKey,
    ignoreCollapsed: true,
    callback: ({ node }) => positions.push(node.position)
  })

  return max(positions)
}

export const increaseCreateFormPosition = (treeData) => {
  const lastPosition = getTreeLastPosition(treeData)

  return mapTree({
    treeData,
    getNodeKey,
    ignoreCollapsed: true,
    callback: ({ node }) => {
      const additional = {}

      if (node.isCreateRoot) {
        additional.position = lastPosition + 1
        additional.treePosition = lastPosition + 1
      }

      return {
        ...node,
        ...additional
      }
    }
  })
}

const createSelfPositioning = () => {
  let position = 0

  return map((object) => {
    position = position + 1

    return {
      ...object,
      position: position,
      treePosition: position
    }
  })
}

export const groupTreeByEntities = (subtree) => {
  const grouped = groupBy('entity')(subtree)

  const updatePositions = createSelfPositioning()
  const helperNodes = safeGetOr([], 'undefined')(grouped)

  // ORDER IS VERY IMPORTANT
  const actions = pipe(safeGetOr([], ENTITIES.PLAN_ACTION), updatePositions)(grouped)

  const kpis = pipe(safeGetOr([], ENTITIES.PLAN_KPI), updatePositions)(grouped)

  const objectives = pipe(safeGetOr([], ENTITIES.PLAN_OBJECTIVE), updatePositions)(grouped)

  const createObjective = pipe(filter({ isCreateObjective: true }), updatePositions, last)(helperNodes)

  const createAction = pipe(filter({ isCreateAction: true }), updatePositions, last)(helperNodes)

  const createKPI = pipe(filter({ isCreateKPI: true }), updatePositions, last)(helperNodes)

  const rootCreateObjective = pipe(filter({ isCreateRoot: true }), updatePositions, last)(helperNodes)

  const positionedSubtree = pipe(
    filter(identity),
    orderBy('position', 'asc')
  )([...actions, ...kpis, ...objectives, createObjective, createAction, createKPI, rootCreateObjective])

  return map((node) => {
    let children = []

    if (node.children && size(node.children)) {
      children = groupTreeByEntities(node.children)
    }

    return {
      ...node,
      children
    }
  })(positionedSubtree)
}

export const isColorLight = (color) => {
  if (!color) return

  const c = color.substring(1) // strip #
  const rgb = parseInt(c, 16) // convert rrggbb to decimal
  const r = (rgb >> 16) & 0xff // extract red
  const g = (rgb >> 8) & 0xff // extract green
  const b = (rgb >> 0) & 0xff // extract blue

  const luma = 0.2126 * r + 0.7152 * g + 0.0722 * b // per ITU-R BT.709

  return luma > 230
}

export const generatePath = (planItem) => {
  const plan = safeGetOr('', 'plan.name')(planItem)
  const path = safeGetOr([], 'path')(planItem)

  return `${plan} ${path.length ? '>' : ''} ${path.join(' > ')}`
}

export const formatTree = (treeData) => {
  const flatTree = getFlatDataFromTree({
    treeData: treeData,
    getNodeKey,
    ignoreCollapsed: false
  })

  return flatTree.map(({ node, parentNode, treeIndex }) => {
    return {
      ...node,
      parentId: get(parentNode, 'id', null),
      position: treeIndex
    }
  })
}

export const collectNodeChilds = (node, childs = []) => {
  childs.push(node.id)

  if (node.children) {
    node.children.forEach((node) => collectNodeChilds(node, childs))
  }
  return childs
}
